import { useMemo, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  CalendarClock,
  CircleHelp,
  Download,
  FileSpreadsheet,
  MinusCircle,
  PackagePlus,
  Search,
  SlidersHorizontal,
  Tags,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Card,
  EmptyState,
  InlineNotice,
  Money,
  PageHeader,
  QuantityControl,
  StatusBadge,
} from "../components/ui";
import { db, type CachedProduct } from "../lib/db";
import { applyLocalAdjustment } from "../lib/local-operations";
import { flushSyncQueue } from "../lib/sync";
import { useAuth } from "../lib/auth";
import { api, ApiError } from "../lib/api";
import { refreshFromServer } from "../lib/data";
import { downloadText, toCsv } from "../lib/csv";

type Filter =
  | "IN_STOCK"
  | "ALL"
  | "LOW"
  | "OUT"
  | "EXPIRY"
  | "EXPIRED";

export function InventoryPage() {
  const auth = useAuth();
  const role = auth.session?.user.role ?? "OWNER";
  const canReceiveStock = ["OWNER", "MANAGER", "STAFF"].includes(role);
  const canAdjustStock = ["OWNER", "MANAGER"].includes(role);
  const products = useLiveQuery(
    () => db.products.orderBy("name").toArray(),
    [],
    [],
  );

  const [query, setQuery] = useState("");
  const [filter, setFilter] =
    useState<Filter>("IN_STOCK");
  const [adjusting, setAdjusting] =
    useState<CachedProduct | null>(null);
  const [actual, setActual] = useState(0);
  const [reason, setReason] =
    useState("STOCKTAKE");
  const [note, setNote] = useState("");
  const [adjustmentUnitCost, setAdjustmentUnitCost] = useState(0);
  const [lotCode, setLotCode] =
    useState("");
  const [expiryDate, setExpiryDate] =
    useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const inventoryMetrics = useMemo(() => {
    const inStockSkus = products.filter((product) => product.stockQty > 0).length;
    const lowStockSkus = products.filter(
      (product) => product.stockQty > 0 && product.stockQty <= product.minStockQty,
    ).length;
    const outOfStockSkus = products.filter((product) => product.stockQty <= 0).length;
    const inventoryValue = products.reduce(
      (total, product) =>
        total + Math.max(0, product.stockQty) * Math.max(0, product.avgCost),
      0,
    );

    return {
      inStockSkus,
      lowStockSkus,
      outOfStockSkus,
      inventoryValue: Math.round(inventoryValue),
    };
  }, [products]);

  function exportInventory() {
    const csv = toCsv(
      [
        "sku",
        "name",
        "businessVertical",
        "category",
        "subcategory",
        "barcode",
        "baseUnit",
        "stockQty",
        "avgCost",
        "inventoryValue",
        "salePrice",
        "minStockQty",
        "trackExpiry",
        "nextExpiryDate",
      ],
      products.map((product) => ({
        sku: product.sku,
        name: product.name,
        businessVertical: product.businessVertical ?? "",
        category: product.category ?? "",
        subcategory: product.subcategory ?? "",
        barcode: product.barcode ?? "",
        baseUnit: product.baseUnit,
        stockQty: product.stockQty,
        avgCost: Math.round(product.avgCost),
        inventoryValue: Math.round(Math.max(0, product.stockQty) * Math.max(0, product.avgCost)),
        salePrice: product.salePrice,
        minStockQty: product.minStockQty,
        trackExpiry: product.trackExpiry ? "true" : "false",
        nextExpiryDate: product.nextExpiryDate ?? "",
      })),
    );

    downloadText(
      `bledger-inventory-${new Date().toISOString().slice(0, 10)}.csv`,
      csv,
      "text/csv;charset=utf-8",
    );
  }

  const filtered = useMemo(() => {
    const needle = query
      .trim()
      .toLocaleLowerCase("vi");

    return products.filter((product) => {
      if (
        needle &&
        !product.name
          .toLocaleLowerCase("vi")
          .includes(needle) &&
        !product.sku
          .toLocaleLowerCase("vi")
          .includes(needle) &&
        !product.barcode?.includes(needle)
      ) {
        return false;
      }

      if (
        filter === "IN_STOCK" &&
        product.stockQty <= 0
      ) {
        return false;
      }

      if (
        filter === "LOW" &&
        !(
          product.stockQty > 0 &&
          product.stockQty <=
            product.minStockQty
        )
      ) {
        return false;
      }

      if (
        filter === "OUT" &&
        product.stockQty > 0
      ) {
        return false;
      }

      if (
        filter === "EXPIRY" &&
        !product.nextExpiryDate &&
        !product.trackExpiry
      ) {
        return false;
      }

      if (filter === "EXPIRED") {
        if (!product.nextExpiryDate) return false;
        const expTime = new Date(product.nextExpiryDate).getTime();
        if (expTime >= Date.now()) return false;
      }

      return true;
    });
  }, [products, query, filter]);

  function openAdjust(
    product: CachedProduct,
  ) {
    setAdjusting(product);
    setActual(product.stockQty);
    setReason("STOCKTAKE");
    setNote("");
    setAdjustmentUnitCost(0);
    setLotCode("");
    setExpiryDate("");
    setError("");
  }

  async function submitAdjustment() {
    if (!adjusting) return;

    const delta =
      Math.round(
        (actual -
          adjusting.stockQty +
          Number.EPSILON) *
          1000,
      ) / 1000;

    if (delta === 0) {
      setAdjusting(null);
      return;
    }

    if (actual < 0) {
      setError(
        "Tồn thực tế không được âm.",
      );
      return;
    }

    if (delta > 0 && adjustmentUnitCost <= 0) {
      setError(
        "Điều chỉnh tăng tồn cần nhập giá vốn trên mỗi " +
          adjusting.baseUnit +
          " để BLedger tính đúng giá vốn bình quân và lợi nhuận.",
      );
      return;
    }

    if (
      delta > 0 &&
      adjusting.trackExpiry &&
      !expiryDate
    ) {
      setError(
        "Tăng tồn cho sản phẩm theo dõi HSD cần nhập hạn sử dụng.",
      );
      return;
    }

    setBusy(true);
    setError("");
    const idempotencyKey =
      crypto.randomUUID();

    const payload = {
      idempotencyKey,
      productId: adjusting.id,
      delta,
      reason,
      note: note.trim() || undefined,
      unitCost: delta > 0 ? Math.round(adjustmentUnitCost) : undefined,
      lotCode: lotCode.trim() || null,
      inventoryLotId: null,
      expiryDate: expiryDate || null,
    };

    try {
      if (auth.demo || !navigator.onLine) {
        await applyLocalAdjustment(
          adjusting,
          delta,
          idempotencyKey,
          {
            reason,
            note: note.trim() || undefined,
            unitCost: delta > 0 ? Math.round(adjustmentUnitCost) : undefined,
            lotCode: lotCode.trim() || null,
            expiryDate: expiryDate || null,
            demo: auth.demo,
            tenantId: auth.session?.tenant?.id,
            storeId: auth.session?.store?.id,
          },
        );
      } else {
        try {
          await api("/inventory/adjust", {
            method: "POST",
            body: JSON.stringify(payload),
          });
          await refreshFromServer();
        } catch (onlineError) {
          if (onlineError instanceof ApiError && onlineError.status < 500) {
            throw onlineError;
          }

          await applyLocalAdjustment(
            adjusting,
            delta,
            idempotencyKey,
            {
              reason,
              note: note.trim() || undefined,
              unitCost: delta > 0 ? Math.round(adjustmentUnitCost) : undefined,
              lotCode: lotCode.trim() || null,
              expiryDate: expiryDate || null,
              demo: false,
              tenantId: auth.session?.tenant?.id,
              storeId: auth.session?.store?.id,
            },
          );

          const retry = await flushSyncQueue(
            auth.session?.tenant?.id,
            auth.session?.store?.id,
          );
          if (retry.failed === 0 && retry.pendingRemaining === 0) {
            await refreshFromServer();
          }
        }
      }

      setAdjusting(null);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể điều chỉnh tồn kho",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="Kho hàng"
        description="Tồn kho thực tế theo SKU. Mặc định chỉ hiển thị các mặt hàng đang có số lượng tồn lớn hơn 0."
        action={
          <div className="header-actions">
            <Link className="btn btn-secondary" to="/help#inventory">
              <CircleHelp size={18} /> Quy trình kho
            </Link>
            <Link className="btn btn-secondary" to="/products">
              <Tags size={18} /> Danh mục SKU
            </Link>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={products.length === 0}
              onClick={exportInventory}
            >
              <Download size={18} /> Xuất tồn kho
            </button>
            {canReceiveStock ? (
              <>
                <Link className="btn btn-secondary" to="/inventory/import">
                  <FileSpreadsheet size={18} /> Nhập từ tệp
                </Link>
                <Link className="btn btn-primary" to="/inventory/receive">
                  <PackagePlus size={18} /> Nhập hàng
                </Link>
              </>
            ) : null}
          </div>
        }
      />

      <div className="inventory-summary-grid">
        <Card className="inventory-summary-card">
          <span>SKU đang có hàng</span>
          <strong>{inventoryMetrics.inStockSkus}</strong>
          <small>Tổng {products.length} SKU trong danh mục</small>
        </Card>
        <Card className="inventory-summary-card">
          <span>Giá trị tồn theo giá vốn</span>
          <Money value={inventoryMetrics.inventoryValue} />
          <small>Σ tồn từng SKU × giá vốn bình quân</small>
        </Card>
        <Card className="inventory-summary-card">
          <span>Sắp hết</span>
          <strong>{inventoryMetrics.lowStockSkus}</strong>
          <small>Đang có hàng nhưng chạm ngưỡng tối thiểu</small>
        </Card>
        <Card className="inventory-summary-card">
          <span>Hết hàng</span>
          <strong>{inventoryMetrics.outOfStockSkus}</strong>
          <small>SKU có tồn hiện tại ≤ 0</small>
        </Card>
      </div>

      <InlineNotice tone="info" title="Cách BLedger tính tồn">
        Tồn hiện tại được lấy từ sổ biến động kho đã ghi nhận. Khai báo Product Master không làm tăng tồn; nhập hàng làm tăng tồn, bán hàng làm giảm tồn và kiểm kê tạo bút toán điều chỉnh. Giá trị tồn dùng giá vốn bình quân của từng SKU.
      </InlineNotice>

      <div className="search-box">
        <Search size={20} />
        <input
          value={query}
          onChange={(event) =>
            setQuery(event.target.value)
          }
          placeholder="Tìm tên, SKU hoặc mã vạch"
        />
      </div>

      <div className="filter-chips">
        {[
          ["IN_STOCK", "Có hàng"],
          ["LOW", "Sắp hết"],
          ["OUT", "Hết hàng"],
          ["ALL", "Tất cả tồn kho"],
          ["EXPIRY", "Có HSD"],
          ["EXPIRED", "Đã hết hạn"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={
              filter === value
                ? "active"
                : ""
            }
            onClick={() =>
              setFilter(value as Filter)
            }
          >
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title={
            products.length === 0
              ? "Chưa có SKU trong danh mục"
              : filter === "IN_STOCK"
                ? "Kho hiện chưa có hàng"
                : "Không có tồn kho phù hợp"
          }
          description={
            products.length === 0
              ? "Tạo SKU trong Danh mục sản phẩm trước, sau đó thực hiện Nhập hàng để phát sinh tồn."
              : filter === "IN_STOCK"
                ? "Các SKU tồn 0 không hiển thị ở chế độ Có hàng. Chọn Hết hàng nếu cần xem để nhập bổ sung."
                : "Thử đổi bộ lọc hoặc từ khóa tìm kiếm."
          }
          action={
            products.length === 0 ? (
              <div className="empty-actions">
                {["OWNER", "MANAGER"].includes(role) ? (
                  <Link className="btn btn-primary" to="/products/new">
                    <Tags size={18} /> Tạo SKU đầu tiên
                  </Link>
                ) : null}
                <Link className="btn btn-secondary" to="/help#inventory">
                  <CircleHelp size={18} /> Xem quy trình chuẩn
                </Link>
              </div>
            ) : undefined
          }
        />
      ) : (
        <div className="product-list">
          {filtered.map((product) => {
            const low =
              product.stockQty <=
              product.minStockQty;
            const expiryLabel =
              product.nextExpiryDate
                ? new Intl.DateTimeFormat(
                    "vi-VN",
                    {
                      timeZone:
                        "Asia/Ho_Chi_Minh",
                    },
                  ).format(
                    new Date(
                      product.nextExpiryDate,
                    ),
                  )
                : null;

            return (
              <Card
                key={product.id}
                className="inventory-row inventory-row-rich"
              >
                <div className="product-avatar">
                  {product.name
                    .slice(0, 1)
                    .toUpperCase()}
                </div>

                <div className="product-main">
                  <strong>
                    {product.name}
                  </strong>

                  <div className="product-meta">
                    <span>
                      Tồn{" "}
                      <b>
                        {product.stockQty}{" "}
                        {product.baseUnit}
                      </b>
                    </span>
                    <Money
                      value={
                        product.salePrice
                      }
                    />
                  </div>

                  <div className="inventory-badges">
                    <StatusBadge
                      tone={
                        product.stockQty <= 0
                          ? "danger"
                          : low
                            ? "warning"
                            : "success"
                      }
                    >
                      {product.stockQty <= 0
                        ? "Hết hàng"
                        : low
                          ? "Sắp hết"
                          : "Còn tốt"}
                    </StatusBadge>

                    {product.units.length >
                    0 ? (
                      <StatusBadge tone="info">
                        {product.units.length}{" "}
                        quy cách
                      </StatusBadge>
                    ) : null}

                    {expiryLabel ? (
                      <StatusBadge tone="warning">
                        HSD {expiryLabel}
                      </StatusBadge>
                    ) : null}
                  </div>

                  {product.units.length >
                  0 ? (
                    <small className="unit-summary">
                      {product.units
                        .map(
                          (unit) =>
                            `1 ${unit.name} = ${unit.multiplier} ${product.baseUnit}`,
                        )
                        .join(" · ")}
                    </small>
                  ) : null}
                </div>

                {canAdjustStock ? (
                  <button
                    className="btn btn-secondary compact"
                    onClick={() =>
                      openAdjust(product)
                    }
                  >
                    <SlidersHorizontal
                      size={17}
                    />{" "}
                    Điều chỉnh
                  </button>
                ) : null}
              </Card>
            );
          })}
        </div>
      )}

      {adjusting ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onMouseDown={() =>
            !busy &&
            setAdjusting(null)
          }
        >
          <div
            className="bottom-sheet"
            role="dialog"
            aria-modal="true"
            onMouseDown={(event) =>
              event.stopPropagation()
            }
          >
            <div className="sheet-handle" />
            <h2>
              Kiểm kê {adjusting.name}
            </h2>

            {error ? (
              <InlineNotice
                tone="danger"
                title={error}
              />
            ) : null}

            <p>
              Hệ thống:{" "}
              <strong>
                {adjusting.stockQty}{" "}
                {adjusting.baseUnit}
              </strong>
            </p>

            <label>
              Tồn thực tế ({adjusting.baseUnit})
              <div style={{ marginTop: 4 }}>
                <QuantityControl
                  value={actual}
                  min={0}
                  step={1}
                  allowDecimal={true}
                  unitName={adjusting.baseUnit}
                  onChange={(val) => setActual(val)}
                />
              </div>
            </label>

            {actual > adjusting.stockQty ? (
              <label>
                Giá vốn / {adjusting.baseUnit} của phần tăng
                <input
                  type="number"
                  min="1"
                  step="100"
                  inputMode="numeric"
                  value={adjustmentUnitCost || ""}
                  onChange={(event) =>
                    setAdjustmentUnitCost(Number(event.target.value) || 0)
                  }
                  placeholder="Nhập giá vốn thực tế"
                  required
                />
                <small>
                  Giá vốn bình quân hiện tại chỉ để tham khảo:{" "}
                  {adjusting.avgCost.toLocaleString("vi-VN")} ₫ / {adjusting.baseUnit}.
                  BLedger sẽ tính lại bình quân gia quyền sau khi xác nhận.
                </small>
              </label>
            ) : null}

            <div className="difference-row">
              <span>Chênh lệch</span>
              <strong
                className={
                  actual -
                    adjusting.stockQty <
                  0
                    ? "text-danger"
                    : "text-success"
                }
              >
                {actual -
                  adjusting.stockQty >
                0
                  ? "+"
                  : ""}
                {Math.round(
                  (actual -
                    adjusting.stockQty) *
                    1000,
                ) / 1000}
              </strong>
            </div>

            <label>
              Lý do điều chỉnh
              <select
                value={reason}
                onChange={(event) =>
                  setReason(
                    event.target.value,
                  )
                }
              >
                <option value="STOCKTAKE">
                  Kiểm kê định kỳ (Cân đối tồn)
                </option>
                <option value="EXPIRED">
                  Hàng hết hạn / Tiêu hủy
                </option>
                <option value="DAMAGED">
                  Hư hỏng
                </option>
                <option value="LOST">
                  Thất thoát
                </option>
                <option value="WRONG_RECEIPT">
                  Nhập sai phiếu
                </option>
                <option value="RETURN">
                  Khách trả hàng
                </option>
                <option value="INITIAL_CORRECTION">
                  Điều chỉnh số dư ban đầu
                </option>
                <option value="OTHER">
                  Lý do khác
                </option>
              </select>
            </label>

            {reason === "OTHER" ? (
              <label>
                Ghi chú chi tiết lý do
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Nhập lý do cụ thể..."
                  maxLength={500}
                  required
                />
              </label>
            ) : null}

            {adjusting.trackExpiry &&
            actual >
              adjusting.stockQty ? (
              <div className="form-grid">
                <label>
                  Mã lô tăng thêm
                  <input
                    value={lotCode}
                    onChange={(event) =>
                      setLotCode(
                        event.target.value,
                      )
                    }
                    placeholder="Không bắt buộc"
                  />
                </label>

                <label>
                  Hạn sử dụng
                  <input
                    type="date"
                    value={expiryDate}
                    onChange={(event) =>
                      setExpiryDate(
                        event.target.value,
                      )
                    }
                    required
                  />
                </label>
              </div>
            ) : null}

            {adjusting.trackExpiry &&
            actual <
              adjusting.stockQty ? (
              <InlineNotice
                tone="info"
                title="Xuất giảm theo FEFO"
              >
                Hệ thống sẽ trừ lô hết hạn sớm
                nhất trước để giữ tồn theo lô
                nhất quán.
              </InlineNotice>
            ) : null}

            <div className="notice notice-info">
              <MinusCircle size={19} />
              <span>
                Mọi điều chỉnh đều sinh movement
                và lịch sử hoạt động; không ghi
                đè tồn kho âm thầm.
              </span>
            </div>

            <button
              className="btn btn-primary btn-lg"
              disabled={
                busy ||
                actual < 0 ||
                (actual > adjusting.stockQty && adjustmentUnitCost <= 0) ||
                (adjusting.trackExpiry &&
                  actual >
                    adjusting.stockQty &&
                  !expiryDate)
              }
              onClick={() =>
                void submitAdjustment()
              }
            >
              <CalendarClock
                size={19}
              />{" "}
              {busy
                ? "Đang lưu..."
                : "Xác nhận điều chỉnh"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
